import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, CircleCheck, CirclePlus, Edit2, Loader2, Minus, Plus, RefreshCw, Save, Trash2, X,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { API_BASE_URL } from '../config/api';
import { useToast } from '../shared/Toast';
import { SheetField, SheetSection } from './sheet';
import { HINT_CLS, INPUT_CLS } from './storeSettingsForm';

/**
 * «Αρίθμηση παραστατικών» (Oxygen): σειρές ανά τύπο παραστατικού, ενεργή σειρά, τελευταίος αριθμός, νέα
 * σειρά, διαγραφή σειράς που δεν αρίθμησε ποτέ και «ίδια σειρά σε όλα τα παραστατικά». Από 13/10/2026 στη
 * δική της σελίδα (DocumentNumberingPage· ως τότε στα «Στοιχεία επιχείρησης», πριν στο ECR → Ρυθμίσεις).
 * Κάθε ενέργεια εφαρμόζεται αμέσως (routes/fiscal.js, μόνο διαχειριστής/υπεύθυνος) — δεν είναι φόρμα· η
 * διαγραφή και η αλλαγή σε όλα θέλουν επιβεβαίωση. Ο τελευταίος αριθμός διαβάζεται από το ΔΙΚΟ του πεδίο
 * (ως 12/10 από το πρώτο number input της σελίδας — querySelector — που μπορούσε να είναι άλλης σειράς)
 * και μόνο ως ακέραιος ≥ 0.
 * Στο «Φύλλο» (13/10/2026): ενότητα «Αλλαγή σειράς σε όλα» και μία ενότητα ανά τύπο με τον πίνακά του.
 */

interface DocumentType {
  id: number;
  name: string;
  invoice_type_code: string | null;
  display_order: number;
}

interface DocumentSequence {
  id: number;
  document_type_id: number;
  document_type_name: string;
  series: number;
  last_number: number;
  is_active: boolean;
  updated_at: string;
}

/** Ένα βήμα του PUT series/activate-all (σχέδιο με dryRun ή αποτέλεσμα). */
interface BulkActivated {
  document_type_id: number;
  document_type_name: string;
  action: 'create' | 'activate' | 'unchanged';
  previous_series: number | null;
  next_number: number;
}

interface BulkSkipped {
  document_type_id: number;
  document_type_name: string;
  reason: string;
  conflicting_type_name?: string;
}

interface BulkPlan {
  series: number;
  activated: BulkActivated[];
  skipped: BulkSkipped[];
}

type Reply = { ok: boolean; status: number; data: any };

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('xentrix_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Κωδικοί σφαλμάτων του server → μήνυμα στη γλώσσα του χειριστή (ο server γράφει ελληνικά).
const ERROR_KEYS: Record<string, string> = {
  SERIES_USED: 'settings:ecr.seriesUsedError',
  SERIES_ACTIVE: 'settings:ecr.seriesActiveError',
  SERIES_CHANGED: 'settings:ecr.seriesChangedError',
  INVALID_SERIES: 'settings:ecr.bulkSeriesInvalid',
  INVALID_LAST_NUMBER: 'settings:ecr.lastNumberInvalid',
};

const INT4_MAX = 2147483647;

/** Σειρά από το πεδίο: ακέραιος 1…INT4_MAX, αλλιώς null. */
const parseSeries = (raw: string): number | null => {
  const text = raw.trim();
  if (!/^\d+$/.test(text)) return null;
  const n = parseInt(text, 10);
  return n >= 1 && n <= INT4_MAX ? n : null;
};

const ACTION_BTN = 'inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors disabled:opacity-50';
const SECONDARY_BTN = 'flex-1 min-h-[44px] px-4 rounded-xl border border-line-strong text-t2 hover:bg-surface-3 text-sm font-medium transition-colors';
const PRIMARY_BTN = 'flex-1 min-h-[44px] px-4 rounded-xl bg-brand text-brand-fg hover:brightness-110 text-sm font-semibold transition disabled:opacity-50';

/** Παράθυρο πάνω από τη σελίδα (κάτω φύλλο στο κινητό): Esc ή κλικ έξω το κλείνει. */
const Dialog: React.FC<{ titleId: string; onClose: () => void; wide?: boolean; children: React.ReactNode }> = ({
  titleId, onClose, wide = false, children,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 sm:p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={e => e.stopPropagation()}
        className={`w-full ${wide ? 'sm:max-w-lg' : 'sm:max-w-md'} max-h-[90vh] overflow-y-auto bg-surface-2 dark:bg-surface-1 border border-line rounded-t-2xl sm:rounded-2xl p-6`}
      >
        {children}
      </div>
    </div>
  );
};

type LoadState = 'loading' | 'ready' | 'error' | 'forbidden';

const DocumentNumberingSection: React.FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const storeId = store?.id;

  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [sequences, setSequences] = useState<DocumentSequence[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<{ sequenceId: number; value: string } | null>(null);
  const [newSeries, setNewSeries] = useState<{ documentTypeId: number; name: string; series: number } | null>(null);
  const [bulkSeries, setBulkSeries] = useState('');
  const [bulkPlan, setBulkPlan] = useState<BulkPlan | null>(null);
  const [deleting, setDeleting] = useState<DocumentSequence | null>(null);

  // Φορτώθηκε ήδη μία φορά: μια αποτυχημένη ξαναφόρτωση (μετά από ενέργεια) κρατά τον πίνακα.
  const loaded = useRef(false);

  const fetchSettings = async () => {
    if (!storeId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/fiscal/stores/${storeId}/ecr-settings`, { headers: authHeaders() });
      if (res.status === 403) {
        setLoadState('forbidden');
        return;
      }
      if (!res.ok) throw new Error(`ecr-settings: ${res.status}`);
      const data = await res.json();
      setDocumentTypes(data.documentTypes || []);
      setSequences(data.sequences || []);
      loaded.current = true;
      setLoadState('ready');
    } catch (error) {
      console.error('Failed to fetch ECR settings:', error);
      if (loaded.current) showToast('error', t('settings:ecr.loadSettingsError'));
      else setLoadState('error');
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const send = async (method: 'PUT' | 'DELETE', path: string, body?: Record<string, unknown>): Promise<Reply> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/fiscal/stores/${storeId}/${path}`, {
        method,
        headers: body ? { 'Content-Type': 'application/json', ...authHeaders() } : authHeaders(),
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const data = await res.json().catch(() => null);
      return { ok: res.ok, status: res.status, data };
    } catch (error) {
      console.error(`Failed: ${method} ${path}`, error);
      return { ok: false, status: 0, data: null };
    }
  };

  /** Μήνυμα αποτυχίας: γνωστός κωδικός → μετάφραση· 403 → μόνο διαχειριστής· 409 → το (ελληνικό) του server. */
  const errorText = ({ status, data }: Reply, fallbackKey: string): string => {
    const key = typeof data?.code === 'string' ? ERROR_KEYS[data.code] : undefined;
    if (key) return t(key, { series: data.series });
    if (status === 403) return t('settings:ecr.adminOnly');
    if (status === 409 && typeof data?.error === 'string') return data.error;
    return t(fallbackKey);
  };

  /** PUT σε endpoint αρίθμησης με μήνυμα επιτυχίας και ξαναφόρτωση· true αν πέτυχε. */
  const put = async (path: string, body: Record<string, unknown>, errorKey: string, success: string): Promise<boolean> => {
    setSaving(true);
    try {
      const reply = await send('PUT', path, body);
      if (!reply.ok) {
        showToast('error', errorText(reply, errorKey));
        return false;
      }
      showToast('success', success);
      await fetchSettings();
      return true;
    } finally {
      setSaving(false);
    }
  };

  const activateSeries = (seq: DocumentSequence) =>
    put('set-active-series', { sequenceId: seq.id }, 'settings:ecr.activateSeriesError',
      t('settings:ecr.seriesActivated', { series: seq.series }));

  const saveLastNumber = async () => {
    if (!editing) return;
    const raw = editing.value.trim();
    if (!/^\d+$/.test(raw)) {
      showToast('error', t('settings:ecr.lastNumberInvalid'));
      return;
    }
    const last = parseInt(raw, 10);
    const ok = await put('reset-sequence', { sequenceId: editing.sequenceId, newLastNumber: last }, 'settings:ecr.resetSequenceError',
      t('settings:ecr.lastNumberSaved', { next: last + 1 }));
    if (ok) setEditing(null);
  };

  const addSeries = async () => {
    if (!newSeries) return;
    const ok = await put('document-series', { documentTypeId: newSeries.documentTypeId, newSeries: newSeries.series }, 'settings:ecr.addSeriesError',
      t('settings:ecr.seriesAdded', { series: newSeries.series }));
    if (ok) setNewSeries(null);
  };

  /** «Συνέχεια»: το σχέδιο από τον server (dryRun, καμία εγγραφή) → παράθυρο επιβεβαίωσης. */
  const previewBulk = async () => {
    if (saving) return;
    const series = parseSeries(bulkSeries);
    if (series === null) {
      showToast('error', t('settings:ecr.bulkSeriesInvalid'));
      return;
    }
    setSaving(true);
    try {
      const reply = await send('PUT', 'series/activate-all', { series, dryRun: true });
      if (!reply.ok) {
        showToast('error', errorText(reply, 'settings:ecr.bulkPreviewError'));
        return;
      }
      setBulkPlan({ series, activated: reply.data?.activated || [], skipped: reply.data?.skipped || [] });
    } finally {
      setSaving(false);
    }
  };

  /** «Εφαρμογή»: η πραγματική αλλαγή· το αποτέλεσμα (όχι το σχέδιο) λέει τι έγινε. */
  const applyBulk = async () => {
    if (!bulkPlan || saving) return;
    const { series } = bulkPlan;
    setSaving(true);
    try {
      const reply = await send('PUT', 'series/activate-all', { series });
      setBulkPlan(null);
      if (reply.ok) {
        const activated: BulkActivated[] = reply.data?.activated || [];
        const skipped: BulkSkipped[] = reply.data?.skipped || [];
        setBulkSeries('');
        showToast('success', t('settings:ecr.bulkApplied', { series, count: activated.length }));
        if (skipped.length > 0) {
          showToast('warning', t('settings:ecr.bulkSkippedToast', { names: skipped.map(s => s.document_type_name).join(', ') }), 8000);
        }
      } else {
        showToast('error', errorText(reply, 'settings:ecr.bulkApplyError'));
      }
      await fetchSettings();
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting || saving) return;
    const seq = deleting;
    setSaving(true);
    try {
      const reply = await send('DELETE', `sequences/${seq.id}`);
      setDeleting(null);
      if (reply.ok) {
        showToast('success', t('settings:ecr.seriesDeleted', { series: seq.series }));
        await fetchSettings();
      } else {
        showToast('error', errorText(reply, 'settings:ecr.deleteError'));
        // 409/404: η σειρά αρίθμησε ή άλλαξε από άλλο παράθυρο στο μεταξύ — δείξε την τωρινή εικόνα.
        if (reply.status === 409 || reply.status === 404) await fetchSettings();
      }
    } finally {
      setSaving(false);
    }
  };

  const byType = sequences.reduce((acc, seq) => {
    (acc[seq.document_type_id] = acc[seq.document_type_id] || []).push(seq);
    return acc;
  }, {} as Record<number, DocumentSequence[]>);

  // Το σχέδιο με τη σειρά των πινάκων (display_order)· τύποι που δεν εμφανίζονται, στο τέλος.
  const typeRank = new Map(documentTypes.map((d, i) => [d.id, i]));
  const rank = (id: number) => typeRank.get(id) ?? Number.MAX_SAFE_INTEGER;
  const planRows = bulkPlan ? [
    ...bulkPlan.activated.map(a => ({
      id: a.document_type_id,
      name: a.document_type_name,
      kind: a.action as BulkActivated['action'] | 'skipped',
      previous: a.action === 'unchanged' ? null : a.previous_series,
      text: a.action === 'create' ? t('settings:ecr.bulkCreate', { series: bulkPlan.series })
        : a.action === 'activate' ? t('settings:ecr.bulkActivate', { series: bulkPlan.series, next: a.next_number })
        : t('settings:ecr.bulkUnchanged'),
    })),
    ...bulkPlan.skipped.map(s => ({
      id: s.document_type_id,
      name: s.document_type_name,
      kind: 'skipped' as const,
      previous: null,
      text: t('settings:ecr.bulkSkipped', { series: bulkPlan.series, other: s.conflicting_type_name || '' }),
    })),
  ].sort((a, b) => rank(a.id) - rank(b.id) || a.id - b.id) : [];
  const bulkChanges = planRows.filter(r => r.kind === 'create' || r.kind === 'activate').length;

  const deleteTip = (seq: DocumentSequence) => {
    if (seq.is_active) return t('settings:ecr.deleteActiveTip');
    if (seq.last_number !== 0) return t('settings:ecr.deleteUsedTip');
    return t('settings:ecr.deleteSeries', { series: seq.series });
  };

  // Φόρτωση, χωρίς δικαίωμα, σφάλμα ή χωρίς τύπους: μία ενότητα με την κατάσταση στη θέση των πινάκων.
  const stateBody = loadState === 'loading' ? (
    <div className="flex items-center gap-2 py-2 text-t3" role="status">
      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">{t('common:loading')}</span>
    </div>
  ) : loadState === 'forbidden' ? (
    <p className="text-sm text-t3" role="alert">{t('settings:ecr.adminOnly')}</p>
  ) : loadState === 'error' ? (
    <div role="alert" className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl border border-red-500/30 bg-red-500/10">
      <AlertTriangle className="w-5 h-5 shrink-0 text-red-600 dark:text-red-400" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-t1">{t('settings:ecr.loadSettingsError')}</p>
        <p className="text-xs text-t3 mt-0.5">{t('settings:ecr.loadErrorHint')}</p>
      </div>
      <button
        type="button"
        onClick={() => { setLoadState('loading'); fetchSettings(); }}
        className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-xl text-sm font-medium border border-line-strong bg-surface-2 text-t1 hover:bg-surface-3 transition"
      >
        <RefreshCw className="w-4 h-4" aria-hidden="true" />
        {t('common:tryAgain')}
      </button>
    </div>
  ) : documentTypes.length === 0 ? (
    <p className="text-sm text-t3">{t('settings:ecr.noDocumentTypes')}</p>
  ) : null;

  const TH = 'px-2.5 py-2 text-left text-xs font-medium text-t3 uppercase whitespace-nowrap';

  return (
    <>
      {loadState === 'ready' && sequences.length > 0 && (
        <SheetSection id="numbering-bulk" title={t('settings:ecr.bulkTitle')} indexLabel={t('settings:ecr.bulkIndex')}
          hint={t('settings:ecr.bulkHint')} testId="bulk-series">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <SheetField label={t('settings:ecr.bulkSeriesLabel')} htmlFor="bulk-series-input" className="sm:w-48">
              <input
                id="bulk-series-input"
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={bulkSeries}
                placeholder={t('settings:ecr.bulkSeriesPlaceholder')}
                onChange={e => setBulkSeries(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && bulkSeries.trim() !== '') previewBulk(); }}
                className={INPUT_CLS}
              />
            </SheetField>
            <button
              type="button"
              onClick={previewBulk}
              disabled={saving || bulkSeries.trim() === ''}
              className="inline-flex items-center justify-center min-h-[44px] px-5 rounded-xl bg-brand text-brand-fg hover:brightness-110 text-sm font-semibold transition disabled:opacity-50"
            >
              {t('settings:ecr.bulkContinue')}
            </button>
          </div>
        </SheetSection>
      )}

      {stateBody !== null ? (
        <SheetSection id="numbering-series" title={t('settings:ecr.seriesByType')} hint={t('settings:ecr.documentNumberingHint')}
          testId="document-numbering">
          {stateBody}
        </SheetSection>
      ) : documentTypes.map(docType => {
        const typeSequences = byType[docType.id] || [];
        return (
          <SheetSection
            key={docType.id}
            id={`numbering-type-${docType.id}`}
            title={docType.name}
            hint={docType.invoice_type_code ? t('settings:ecr.typeCode', { code: docType.invoice_type_code }) : undefined}
            testId={`doc-type-${docType.id}`}
            aside={(
              <button
                type="button"
                onClick={() => setNewSeries({ documentTypeId: docType.id, name: docType.name, series: 2 })}
                disabled={saving}
                className="inline-flex items-center gap-1 min-h-[36px] px-3 rounded-lg border border-line-strong text-t2 hover:bg-surface-3 text-sm font-medium transition-colors disabled:opacity-50"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {t('settings:ecr.addSeries')}
              </button>
            )}
          >
            {typeSequences.length === 0 ? (
              <p className="text-sm text-t4 italic">{t('settings:ecr.noSeries')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-line">
                  <thead>
                    <tr>
                      <th className={TH}>{t('settings:ecr.active')}</th>
                      <th className={TH}>{t('settings:ecr.seriesColumn')}</th>
                      <th className={TH}>{t('settings:ecr.lastNumber')}</th>
                      <th className={TH}>{t('settings:ecr.nextNumber')}</th>
                      <th className={TH}>{t('common:actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {typeSequences.map(seq => {
                      const isEditing = editing?.sequenceId === seq.id;
                      const seriesName = t('settings:ecr.seriesName', { series: seq.series });
                      const deletable = seq.last_number === 0 && !seq.is_active;
                      const tip = deleteTip(seq);
                      return (
                        <tr key={seq.id} className={seq.is_active ? 'bg-brand-soft/60' : ''}>
                          <td className="px-2.5 py-2">
                            <input
                              type="radio"
                              name={`active-series-${docType.id}`}
                              checked={seq.is_active}
                              onChange={() => activateSeries(seq)}
                              disabled={saving}
                              aria-label={seriesName}
                              className="h-4 w-4 accent-[hsl(var(--brand))]"
                            />
                          </td>
                          {/* Η τελευταία ενημέρωση κάτω από τη σειρά: πέντε στήλες χωρούν στη δεξιά στήλη του φύλλου. */}
                          <td className="px-2.5 py-2 text-sm font-medium text-t1 whitespace-nowrap">
                            {seriesName}
                            {seq.is_active && (
                              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-brand-soft text-brand">{t('settings:ecr.active')}</span>
                            )}
                            <span className="block text-xs font-normal text-t4 tabular-nums">
                              <span className="sr-only">{t('settings:ecr.lastUpdate')}: </span>
                              {new Date(seq.updated_at).toLocaleString('el-GR')}
                            </span>
                          </td>
                          <td className="px-2.5 py-2 text-sm text-t2 tabular-nums">
                            {isEditing ? (
                              <input
                                type="number"
                                min="0"
                                step="1"
                                inputMode="numeric"
                                autoFocus
                                value={editing.value}
                                aria-label={t('settings:ecr.lastNumber')}
                                onChange={e => setEditing({ sequenceId: seq.id, value: e.target.value })}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') saveLastNumber();
                                  else if (e.key === 'Escape') setEditing(null);
                                }}
                                className="w-24 px-2 py-1 rounded-lg border border-line-strong bg-surface-2 text-t1 focus:outline-none focus:ring-2 focus:ring-ring/50"
                              />
                            ) : (
                              seq.last_number
                            )}
                          </td>
                          <td className="px-2.5 py-2 text-sm font-semibold text-brand tabular-nums">{seq.last_number + 1}</td>
                          <td className="px-2.5 py-2 text-sm">
                            {isEditing ? (
                              <div className="flex gap-1">
                                <button type="button" onClick={saveLastNumber} disabled={saving}
                                  aria-label={t('settings:ecr.saveLastNumber')}
                                  className={`${ACTION_BTN} text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10`}>
                                  <Save className="h-4 w-4" aria-hidden="true" />
                                </button>
                                <button type="button" onClick={() => setEditing(null)} disabled={saving}
                                  aria-label={t('common:cancel')}
                                  className={`${ACTION_BTN} text-red-600 dark:text-red-400 hover:bg-red-500/10`}>
                                  <X className="h-4 w-4" aria-hidden="true" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-1">
                                <button type="button" onClick={() => setEditing({ sequenceId: seq.id, value: String(seq.last_number) })}
                                  disabled={saving}
                                  aria-label={t('settings:ecr.editLastNumber', { series: seq.series })}
                                  className={`${ACTION_BTN} text-brand hover:bg-brand-soft`}>
                                  <Edit2 className="h-4 w-4" aria-hidden="true" />
                                </button>
                                {/* title και στο span: σε ανενεργό κουμπί κάποιοι browsers δεν δείχνουν tooltip */}
                                <span title={tip} className="inline-flex">
                                  <button type="button" onClick={() => setDeleting(seq)}
                                    disabled={saving || !deletable}
                                    aria-label={t('settings:ecr.deleteSeries', { series: seq.series })}
                                    title={tip}
                                    className={`${ACTION_BTN} text-red-600 dark:text-red-400 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:hover:bg-transparent`}>
                                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                                  </button>
                                </span>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </SheetSection>
        );
      })}

      {newSeries && (
        <Dialog titleId="new-series-title" onClose={() => setNewSeries(null)}>
          <h3 id="new-series-title" className="text-lg font-bold text-t1 mb-1">{t('settings:ecr.addNewSeries')}</h3>
          <p className="text-sm text-t3 mb-4">{t('settings:ecr.addSeriesFor', { name: newSeries.name })}</p>
          <label htmlFor="series-number" className="block text-sm font-medium text-t2">{t('settings:ecr.seriesNumber')}</label>
          <input
            id="series-number"
            type="number"
            min="1"
            value={newSeries.series}
            onChange={e => setNewSeries({ ...newSeries, series: parseInt(e.target.value, 10) || 1 })}
            className="mt-1 w-full px-3 py-2 rounded-xl border border-line-strong bg-surface-2 text-t1 focus:outline-none focus:ring-2 focus:ring-ring/50"
          />
          <p className="text-xs text-t3 mt-1">{t('settings:ecr.seriesStartHint')}</p>
          <div className="flex gap-3 mt-5">
            <button type="button" onClick={() => setNewSeries(null)} className={SECONDARY_BTN}>
              {t('common:cancel')}
            </button>
            <button type="button" onClick={addSeries} disabled={saving} className={PRIMARY_BTN}>
              {saving ? t('settings:ecr.addingSeriesBtn') : t('settings:ecr.addSeries')}
            </button>
          </div>
        </Dialog>
      )}

      {bulkPlan && (
        <Dialog titleId="bulk-series-title" onClose={() => setBulkPlan(null)} wide>
          <h3 id="bulk-series-title" className="text-lg font-bold text-t1 mb-1">{t('settings:ecr.bulkSheetTitle', { series: bulkPlan.series })}</h3>
          <p className="text-sm text-t3 mb-4">{t('settings:ecr.bulkSheetIntro')}</p>
          <ul className="rounded-xl border border-line divide-y divide-line">
            {planRows.map(row => {
              const Icon = row.kind === 'create' ? CirclePlus : row.kind === 'activate' ? CircleCheck
                : row.kind === 'skipped' ? AlertTriangle : Minus;
              const tone = row.kind === 'skipped' ? 'text-amber-700 dark:text-amber-400'
                : row.kind === 'unchanged' ? 'text-t4' : 'text-brand';
              return (
                <li key={`${row.kind}-${row.id}`} data-testid={`bulk-plan-${row.id}`} className="flex items-start gap-3 px-4 py-3">
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tone}`} aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-t1">{row.name}</p>
                    <p className={`text-xs mt-0.5 ${row.kind === 'skipped' ? tone : 'text-t3'}`}>{row.text}</p>
                  </div>
                  {row.previous !== null && (
                    <span className="shrink-0 text-xs text-t4 whitespace-nowrap">{t('settings:ecr.bulkPrevious', { series: row.previous })}</span>
                  )}
                </li>
              );
            })}
          </ul>
          {bulkChanges === 0 ? (
            <p className="mt-3 text-sm font-medium text-t2">{t('settings:ecr.bulkNothing')}</p>
          ) : (
            <p className={`${HINT_CLS} mt-3`}>{t('settings:ecr.bulkKeepsOld')}</p>
          )}
          {bulkPlan.skipped.length > 0 && (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{t('settings:ecr.bulkSkippedHint')}</p>
          )}
          <div className="flex gap-3 mt-5">
            <button type="button" autoFocus onClick={() => setBulkPlan(null)} className={SECONDARY_BTN}>
              {t('common:cancel')}
            </button>
            <button type="button" onClick={applyBulk} disabled={saving || bulkChanges === 0} className={PRIMARY_BTN}>
              {saving ? t('settings:ecr.bulkApplying') : t('common:apply')}
            </button>
          </div>
        </Dialog>
      )}

      {deleting && (
        <Dialog titleId="delete-series-title" onClose={() => setDeleting(null)}>
          <h3 id="delete-series-title" className="text-lg font-bold text-t1 mb-1">{t('settings:ecr.deleteConfirmTitle', { series: deleting.series })}</h3>
          <p className="text-sm text-t3">{t('settings:ecr.deleteConfirmText', { name: deleting.document_type_name, series: deleting.series })}</p>
          <div className="flex gap-3 mt-5">
            <button type="button" autoFocus onClick={() => setDeleting(null)} className={SECONDARY_BTN}>
              {t('common:cancel')}
            </button>
            <button type="button" onClick={confirmDelete} disabled={saving}
              className="flex-1 min-h-[44px] px-4 rounded-xl bg-red-600 text-white hover:bg-red-500 text-sm font-semibold transition disabled:opacity-50">
              {t('common:delete')}
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default DocumentNumberingSection;
